import { Icon, Typography } from '@smartgift/frontend-toolkit';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AppLoader } from '@hero/atoms';
import {
  RECOGNITION_CREATION_STEPS,
  DEFAULT_CONTACT_LIMIT_LIST,
  CONTACT_LIMIT_LIST,
  CONTACT_TYPE,
  CONTACTLESS_VERIFICATION_TYPES
} from '@hero/constants';
import { useRecognitionCreationHeader } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { SendWithoutContactCard, DoubleContactTable } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetMerchant } from '@hero/services/merchant-service/hooks';
import {
  useGetRecognitionRecipientList,
  useRecogRecipientsBulkUpsert,
  useRecogRecipientsDelete,
  useUpdateRecognition
} from '@hero/services/order-service';
import { useNewRecognitionStore, useNotification } from '@hero/store/hooks';
import { useValidateRules } from '@hero/store/hooks/useValidateRules';
import { getContactsRule, getRecipientVerificationRule } from '@hero/validations';

import * as S from './style';

// TODO: Will be removed
function NewRecognitionRecipients() {
  const navigate = useNavigate();
  const { data: merchantData } = useGetMerchant({ options: { includeConfigs: true } });
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const {
    code,
    contacts,
    settings: { recipientVerification },
    updateContacts
  } = useNewRecognitionStore();
  const { mutateAsync: updateRecognition, isLoading: isLoadingUpdateRecognition } =
    useUpdateRecognition();
  const { mutateAsync: recipientBulkUpsert } = useRecogRecipientsBulkUpsert();
  const { mutateAsync: recipientDelete } = useRecogRecipientsDelete();

  const { data: recognitionRecipients } = useGetRecognitionRecipientList(
    {
      code: code,
      options: {
        pagination: false,
        select: 'contact.id'
      }
    },
    {
      enabled: !!code
    }
  );

  const [isPageLoading, setIsPageLoading] = React.useState(false);
  const [defaultAddedRows, setDefaultAddedRows] = useState({});

  const contactListLimit = useMemo(() => {
    return merchantData?.configs[CONTACT_LIMIT_LIST] ?? DEFAULT_CONTACT_LIMIT_LIST;
  }, [merchantData]);

  const { validate } = useValidateRules();

  async function handleSaveContacts() {
    const contactIds = Object.keys(defaultAddedRows || {});
    if (contactIds?.length && contacts.type === CONTACT_TYPE.list) {
      await recipientBulkUpsert({
        params: {
          code: code
        },
        contactIds,
        recipientContactIds: recognitionRecipients.docs
          .map(doc => doc?.contact?.id)
          .filter(contactId => contactId)
      });
    }
  }

  const header = useRecognitionCreationHeader({
    step: 3,
    setIsPageLoading,
    onSave: handleSaveContacts
  });

  const handleUpdateAndNavigate = async () => {
    setIsPageLoading(true);

    try {
      await handleSaveContacts();

      if (
        contacts.type === CONTACT_TYPE.number &&
        recipientVerification?.type !== CONTACTLESS_VERIFICATION_TYPES.EMAIL_LIST
      ) {
        await recipientDelete({
          code
        });
      }

      await updateRecognition({
        params: {
          recognitionCode: code
        },
        body: {
          contacts,
          meta: {
            creationStep: RECOGNITION_CREATION_STEPS.newRecognitionExperience
          },
          settings: {
            recipientVerification
          }
        }
      });

      navigate(ROUTE_PATHS.NEW_RECOGNITION_EXPERIENCE);
    } finally {
      setIsPageLoading(false);
    }
  };

  const onNextButtonClick = async () => {
    const contactIds = Object.keys(defaultAddedRows || {});
    const maxCount = contacts.type === CONTACT_TYPE.list ? contactIds?.length : contacts.maxCount;
    const validContacts = {
      ...contacts,
      maxCount
    };
    const isValid = await validate([
      { rule: getContactsRule(), value: validContacts },
      { rule: getRecipientVerificationRule(validContacts), value: recipientVerification }
    ]);

    if (!isValid) return;

    handleUpdateAndNavigate();
  };

  useEffect(() => {
    if (!code) {
      navigate(ROUTE_PATHS.MY_RECOGNITIONS);
    }
  }, []);

  useEffect(() => {
    if (recognitionRecipients)
      setDefaultAddedRows(
        recognitionRecipients.docs
          .map(doc => doc?.contact?.id)
          .filter(contactId => contactId)
          .reduce((result, id) => {
            result[id] = true;
            return result;
          }, {}) || {}
      );
  }, [recognitionRecipients]);

  return (
    <PageLayout
      header={header}
      navigation={{
        rightButton: {
          children: t('next'),
          onClick: onNextButtonClick,
          loading: isLoadingUpdateRecognition,
          icon: true
        },
        leftButton: {
          children: t('back'),
          onClick: () => navigate(ROUTE_PATHS.NEW_RECOGNITION_CONTACT_TYPE)
        }
      }}
    >
      <S.Wrapper>
        <Typography variant="subtitle4Bold" style={{ marginBottom: 16 }}>
          {t('addYourRecipientWithContactHeading')}
        </Typography>
        <S.Content>
          {contacts.type === CONTACT_TYPE.list ? (
            <DoubleContactTable
              featureType="recognition"
              selectedRows={defaultAddedRows}
              onChangeSelectedRows={selectedRows => {
                if (Object.keys(selectedRows ?? {})?.length > contactListLimit) {
                  showNotification({
                    message: t('recipientLimitMessage', {
                      recipientLimit: contactListLimit
                    }),
                    title: t('recipientLimitTitle'),
                    notificationImage: (
                      <Icon name="error" size="xxl" color="var(--color-error-default)" />
                    ),
                    primaryButtonProps: {
                      style: {
                        margin: '0 auto'
                      }
                    }
                  });

                  return;
                }

                updateContacts({
                  maxCount: Object.keys(selectedRows ?? {})?.length,
                  type: contacts?.type
                });

                setDefaultAddedRows(selectedRows);
              }}
            />
          ) : (
            <SendWithoutContactCard />
          )}
        </S.Content>
      </S.Wrapper>
      {isPageLoading && <AppLoader />}
    </PageLayout>
  );
}

export { NewRecognitionRecipients };
